/**
 * =============================================================================
 * ROASTBREW - PLAYBACK CAPABILITY DETECTION
 * =============================================================================
 *
 * The single source of truth for HOW PLAYBACK FUNCTIONS on this browser:
 * which DRM it can actually drive, and therefore which player to mount. Pure,
 * side-effect-free functions so the decision tree can be unit-tested with no
 * DOM and no player.
 *
 * ── FUNCTION, NOT LOOKS ──────────────────────────────────────────────────────
 * This is deliberately SEPARATE from AppLayout's isMobile/isTablet/isDesktop.
 * They answer different questions and must never be merged:
 *
 *   AppLayout (isMobile/…)  -> "how does it LOOK": viewport WIDTH, changes on
 *                              resize/rotate, drives layout (sidebar, spacing).
 *   PlaybackCapability      -> "how does it FUNCTION": what can this browser
 *                              DECRYPT, which player. Fixed for the session.
 *
 * ── `device` IS CONTENT COPY, NOT ROUTING ────────────────────────────────────
 * The `device` field ('phone' | 'tablet' | 'desktop') is HARDWARE class, derived
 * from the device itself (UA + touch points), NOT from window width. It exists
 * for exactly ONE purpose: choosing the wording of the "unsupported" redirect
 * message (a phone can only be told "open in Safari"; a desktop can be told
 * "open in Safari or on desktop"). It must NEVER enter a routing or playback
 * decision — the router reads `player` and nothing else. An iPad at a narrow
 * window is still a 'tablet' device; a desktop at a narrow window is still
 * 'desktop'. That device answer is what the redirect copy needs; layout keeps
 * using AppLayout's viewport isMobile for how things LOOK.
 *
 * ── ALSO SEPARATE FROM device.js ─────────────────────────────────────────────
 * utils/device.js does UA-based browser/OS naming for AUTH SESSION FINGERPRINTING
 * sent to the backend. It has no DRM/CDM detection. Different consumer, different
 * cadence (cached once for auth vs. read by the video router). Kept apart so
 * neither muddies the other.
 *
 * ── CAPABILITY OVER USER-AGENT ───────────────────────────────────────────────
 * UA strings lie (Chromium ships "Safari"/"AppleWebKit"; iOS 26 freezes the OS
 * version). We probe what the browser can DO. For ROUTING, UA is touched in
 * exactly one place: detectAppleMobile, to tell Apple hardware whose FairPlay
 * probe failed apart from a desktop Widevine browser, because "I am an iPhone"
 * has no positive capability to feature-detect. Even there it only picks the
 * failure message; it never routes real playback.
 *
 * ── …AND WHERE CAPABILITY DETECTION CANNOT HELP AT ALL ───────────────────────
 * MEASURED on iPhone Chrome: player 'videojs', hasFairPlay true,
 * WebKitMediaKeys.isTypeSupported('com.apple.fps.1_0', 'video/mp4') === true.
 * That is the CORRECT answer, not a detection bug.
 *
 * Apple requires every iOS browser to render with WebKit. Chrome, Firefox, Edge
 * and every in-app webview on iOS are WKWebView shells around the same engine
 * Safari uses, so they inherit the same FairPlay CDM. There is NO capability
 * difference between them and Safari. Video really does play in iOS Chrome.
 *
 * This module therefore answers TWO DIFFERENT QUESTIONS, and they must not be
 * conflated:
 *
 *   1. WHAT CAN THIS ENGINE DO?          → drm, player, hasFairPlay
 *      Answered by capability probes. Truthful description of the engine.
 *      CONSUMED BY: VideoViewRouter, to pick a player.
 *
 *   2. WHICH BROWSER IS THIS?            → isIosNonSafari
 *      Answerable ONLY by user-agent string, because on iOS there is no
 *      capability difference to find. See detectIosNonSafari below.
 *      CONSUMED BY: UnsupportedBrowserGate, to decide admission.
 *
 * ── WHY THE ROUTER DOES NOT SEE isIosNonSafari ───────────────────────────────
 * Deliberate. UnsupportedBrowserGate wraps the whole app ABOVE the router, so a
 * blocked browser never reaches VideoViewRouter — a branch there would be dead
 * code. More importantly, if the gate is ever bypassed (?nogate=1) or the policy
 * is relaxed, the RIGHT destination for iOS Chrome is the FairPlay page, which is
 * exactly where player === 'videojs' already sends it. Routing on policy would
 * break the escape hatch: the user would get into the app and then hit an
 * "open in Safari" prompt on a browser that can actually play.
 *
 *   Router = capability = "which engine drives this?"
 *   Gate   = policy     = "do we admit this browser?"
 *
 * Keep them on separate signals and each stays correct without the other.
 *
 * ── iOS NON-SAFARI IS TESTED AND WORKING — DO NOT RE-BLOCK IT ────────────────
 * For a while UnsupportedBrowserGate blocked every iOS non-Safari browser, on the
 * assumption recorded in the old comments here that they had no CDM.
 *
 * That has now been TESTED ON DEVICE and the assumption was wrong. iOS Chrome
 * reports player 'videojs', routes to the FairPlay mobile page, and plays the
 * muxed SAMPLE-AES package WITH AUDIO — indistinguishable from iOS Safari. Of
 * course it does: they are WKWebView shells around the same engine.
 *
 * The symptom that made it look broken was a UI bug in the mobile player (a
 * shared touch/click dedupe timestamp swallowed the second tap of a
 * reveal-then-press sequence, so MUTE could not be pressed). Playback was fine
 * the whole time.
 *
 * `isIosNonSafari` below is therefore accurate but UNUSED. It is kept because it
 * is the right switch to reach for if one specific iOS browser ever does break —
 * add it back to the `blocked` expression in UnsupportedBrowserGate and nothing
 * else has to change. Do not re-enable it on the old assumption.
 *
 * ── NATIVE APPS ARE NOT HERE ─────────────────────────────────────────────────
 * The iOS/Android apps are separate, purpose-built binaries that never run this
 * web bundle. There is no "am I in the app?" web check. This module returns web
 * verdicts only.
 * =============================================================================
 */

// ── raw capability probes ────────────────────────────────────────────────────

/**
 * True only where FairPlay can ACTUALLY decrypt: WebKitMediaKeys exists AND the
 * legacy key system we decrypt with reports supported. Every Safari (desktop +
 * iOS) passes; Chrome/Firefox/Edge/Android fail.
 *
 * CRITICAL: probes 'com.apple.fps.1_0', NOT 'com.apple.fps'. The bare alias
 * returns true from isTypeSupported but does NOT enable decryption — only '.1_0'
 * does (shaka-player #9794). Testing the alias is the exact trap that cost days.
 */
export function detectFairPlay(win = typeof window !== 'undefined' ? window : {}) {
  try {
    const WMK = win.WebKitMediaKeys;
    if (!WMK || typeof WMK.isTypeSupported !== 'function') return false;
    return WMK.isTypeSupported('com.apple.fps.1_0', 'video/mp4');
  } catch {
    return false;
  }
}

/**
 * Standard EME entry point Widevine needs. Not conclusive alone (Safari exposes
 * it too), so the decision tree checks FairPlay FIRST and treats "not FairPlay"
 * as the Widevine branch. Exposed for logging/diagnostics.
 */
export function detectEme(nav = typeof navigator !== 'undefined' ? navigator : {}) {
  return typeof nav.requestMediaKeySystemAccess === 'function';
}

/**
 * Apple mobile/tablet hardware, INCLUDING an iPad masquerading as a Mac (iPadOS
 * reports platform 'MacIntel'; touch-point count unmasks it). A hardware fact
 * only — it says NOTHING about which browser or what it can decrypt, and is TRUE
 * on iPhone/iPad Safari, which works fine. Used for two things: the fallback
 * branch below (Apple hardware whose FairPlay probe failed), and as the
 * precondition of detectIosNonSafari.
 */
export function detectAppleMobile(nav = typeof navigator !== 'undefined' ? navigator : {}) {
  const ua = nav.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  if (nav.platform === 'MacIntel' && (nav.maxTouchPoints || 0) > 1) return true; // iPad-as-Mac
  return false;
}

/**
 * HARDWARE class: 'phone' | 'tablet' | 'desktop'. Derived from the device itself
 * so it is stable regardless of window size. Selects redirect-message wording AND,
 * since Release C, the video page's layout (videoViewRouting.js) — the header's
 * "copy only" note predates the tablet pages; the router documents the exception.
 *   - iPad (incl. iPad-as-Mac), Android tablet     -> 'tablet'
 *   - iPhone/iPod, Android phone, other mobile      -> 'phone'
 *   - a desktop user agent on a touch-only device   -> by the screen's short side (Release C)
 *   - everything else                               -> 'desktop'
 */
export function detectDevice(nav = typeof navigator !== 'undefined' ? navigator : {}, win = typeof window !== 'undefined' ? window : {}) {
  const ua = nav.userAgent || '';
  const isIpad = /iPad/.test(ua) || (nav.platform === 'MacIntel' && (nav.maxTouchPoints || 0) > 1);
  if (isIpad) return 'tablet';
  if (/Android/.test(ua) && !/Mobile/.test(ua)) return 'tablet'; // Android tablet = Android w/o Mobile
  if (/iPhone|iPod/.test(ua) || /Android.*Mobile|windows phone|BlackBerry|Opera Mini|IEMobile/i.test(ua)) {
    return 'phone';
  }
  // Release C (3.3): a touch device whose browser presents itself as a desktop ("Request desktop site", some
  // webviews). Only reached when the user agent says desktop, and only signals that don't change with the window.
  // First the device's own screen: a touch-only primary pointer (touch points, coarse, no hover) is classified by
  // the SCREEN's short side (under 600 px = phone, as Android's own sw600dp tablet boundary). Only then Chrome's
  // UA-CH mobile flag, as a fallback: the screen is the more direct signal, and a single flag must not outrank it.
  const mq = (q) => { try { return !!(win.matchMedia && win.matchMedia(q).matches); } catch (e) { return false; } };
  if ((nav.maxTouchPoints || 0) > 0 && mq('(pointer: coarse)') && mq('(hover: none)')) {
    const sc = win.screen || {};
    const short = Math.min(sc.width || 0, sc.height || 0);
    if (short > 0) return short < 600 ? 'phone' : 'tablet';
  }
  if (nav.userAgentData && nav.userAgentData.mobile === true) return 'phone';
  return 'desktop';
}

// ── IDENTITY, NOT CAPABILITY ─────────────────────────────────────────────────
// MEASURED: iOS Chrome reports player 'videojs' and isTypeSupported('com.apple.
// fps.1_0') === true. That is CORRECT, not a bug — Apple requires every iOS
// browser to use WebKit, so Chrome/Firefox/Edge on iOS are WKWebView shells that
// inherit Safari's FairPlay CDM. There is NO capability difference to probe.
//
// So "which browser is this?" cannot be answered by feature detection, only by
// user-agent string. This function is deliberately quarantined from the
// capability probes above and MUST NOT feed `drm` or `player` — those describe
// what the engine CAN DO, and the answer really is "FairPlay, via videojs".
// This is a POLICY input: ship-or-block is a product decision, not a technical
// limit. It is CURRENTLY UNUSED — iOS Chrome was tested and plays fine, so the
// gate no longer blocks on it (see the header). Kept as a ready-made switch.
//
// HOW IT WORKS — a denylist plus a positive signal, because neither alone is
// enough:
//   1. Known third-party iOS browsers stamp a token: CriOS (Chrome), FxiOS
//      (Firefox), EdgiOS (Edge), OPiOS/OPT (Opera), DuckDuckGo, YaBrowser.
//   2. Embedded webviews (Instagram, Facebook, Twitter, generic in-app browsers)
//      often stamp NOTHING identifying. They are caught by the absence of a
//      positive signal instead: real Mobile Safari always carries BOTH
//      'Version/<n>' and 'Safari/<n>'. A bare WKWebView typically has neither.
//
// THIS IS A HEURISTIC AND WILL DRIFT. New browsers appear, tokens change, and
// some in-app webviews imitate Safari closely enough to pass. Treat a false
// negative (someone gets in who should not) as expected wear, and keep the
// gate's bypass hatch available for false positives.
export function detectIosNonSafari(nav = typeof navigator !== 'undefined' ? navigator : {}) {
  const ua = nav.userAgent || '';
  if (!detectAppleMobile(nav)) return false;          // not Apple hardware → not our case

  // 1. Named third-party iOS browsers.
  if (/CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|DuckDuckGo|YaBrowser|Puffin|Coast/i.test(ua)) return true;

  // 2. Anything lacking Safari's own positive markers is an embedded webview.
  const looksLikeSafari = /Version\/[\d.]+/.test(ua) && /Safari\/[\d.]+/.test(ua);
  if (!looksLikeSafari) return true;

  return false;                                        // real Mobile Safari
}

// ── the decision tree ─────────────────────────────────────────────────────────

/**
 * Resolve the whole playback-capability verdict in one synchronous pass.
 * Returns a frozen object:
 *
 *   {
 *     drm:     'fairplay' | 'widevine' | 'none',
 *     player:  'videojs'  | 'shaka'    | 'unsupported',
 *     device:  'phone'    | 'tablet'   | 'desktop',   // COPY ONLY — never routes
 *     isAppleMobile: boolean,                         // raw, logging
 *     isIosNonSafari: boolean,                        // POLICY ONLY — gate input, never routes
 *     override: 'videojs' | 'shaka' | null,           // ?player=, logging
 *     hasFairPlay: boolean,                           // raw probe, logging
 *   }
 *
 * player:
 *   'videojs'     -> FairPlay engine present. Safari, and ALSO every iOS browser,
 *                    since they are all WebKit. Video.js keys the audio track.
 *   'shaka'       -> Widevine browser. Current, working path.
 *   'unsupported' -> Apple hardware whose FairPlay probe FAILED. Note this is now
 *                    a rare/possibly-empty case: iOS browsers are WKWebView shells
 *                    and normally DO report FairPlay (measured on iPhone Chrome).
 *                    It is NOT the iOS-non-Safari bucket — that is isIosNonSafari,
 *                    a policy flag consumed only by UnsupportedBrowserGate.
 *
 * The ?player= override always wins (A/B on one device) but can NEVER rescue
 * 'unsupported' into a player — it cannot conjure a CDM that does not exist.
 *
 * All inputs are injectable so this is unit-testable with no browser.
 */
export function resolvePlaybackCapability({
  win = typeof window !== 'undefined' ? window : {},
  nav = typeof navigator !== 'undefined' ? navigator : {},
  search = typeof window !== 'undefined' ? window.location.search : '',
} = {}) {
  let override = null;
  try {
    const p = new URLSearchParams(search).get('player');
    if (p === 'videojs' || p === 'shaka') override = p;
  } catch {
    /* no search string available — ignore */
  }

  const hasFairPlay = detectFairPlay(win);
  const isAppleMobile = detectAppleMobile(nav);
  const device = detectDevice(nav, win);
  // POLICY signal only — see detectIosNonSafari. Deliberately NOT used in the
  // drm/player branches below: those stay a truthful description of the engine.
  const isIosNonSafari = detectIosNonSafari(nav);

  let drm;
  let player;
  if (hasFairPlay) {
    drm = 'fairplay';
    player = 'videojs';
  } else if (isAppleMobile) {
    // Apple hardware whose FairPlay probe FAILED. Expected to be rare: iOS
    // browsers are WKWebView shells and normally DO report FairPlay (measured on
    // iPhone Chrome — see the header). This branch is the genuine "Apple device,
    // no usable CDM" case, e.g. a locked-down webview or a future OS change.
    // It is NOT "iOS non-Safari" — that is isIosNonSafari, a policy flag.
    drm = 'none';
    player = 'unsupported';
  } else {
    // Desktop Chrome/Edge/Firefox + Android = Widevine.
    drm = 'widevine';
    player = 'shaka';
  }

  // Override swaps the PLAYER only, and never rescues 'unsupported'.
  if (override && player !== 'unsupported') {
    player = override;
  }

  return Object.freeze({
    drm,
    player,
    device,
    isAppleMobile,
    isIosNonSafari,
    override,
    hasFairPlay,
  });
}

export default resolvePlaybackCapability;
